import { useMutation } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  requestOtpApiV1AuthRequestOtpPost,
  verifyOtpApiV1AuthVerifyOtpPost,
} from "@/api/generated/sdk.gen";
import type { TokenResponseSchema } from "@/api/generated/types.gen";

/**
 * Request a one-time sign-in code for an external user's email.
 * The backend response is intentionally generic (no leak of approval status).
 */
export const useRequestOtp = () =>
  useMutation({
    mutationFn: async (email: string): Promise<string> => {
      const response = await requestOtpApiV1AuthRequestOtpPost({
        client: apiClient,
        body: { email },
      });
      if ("error" in response && response.error) {
        throw new Error("Could not send a code. Please try again.");
      }
      return response.data?.message ?? "";
    },
  });

/**
 * Verify a one-time code and receive an app-issued session token.
 */
export const useVerifyOtp = () =>
  useMutation({
    mutationFn: async ({
      email,
      code,
    }: {
      email: string;
      code: string;
    }): Promise<TokenResponseSchema> => {
      const response = await verifyOtpApiV1AuthVerifyOtpPost({
        client: apiClient,
        body: { email, code },
      });
      if ("error" in response && response.error) {
        throw new Error(
          (response.error as { detail?: string })?.detail ||
            "Invalid or expired code."
        );
      }
      if (!response.data) {
        throw new Error("Invalid or expired code.");
      }
      return response.data;
    },
  });
